// ─────────────────────────────────────────────────────────────────────────────
// Customer pricing card (customer profile → Orders tab) + price tier manager.
//
// A customer gets EITHER a price tier OR individual custom pricing (item
// prices/deposits + delivery charges) — the card enforces the exclusivity
// visually and the DB triggers enforce it hard. Effective values apply
// everywhere lines are created: staff orders, counter sales, and the online
// store when the customer is signed in.
//
// Units: item prices and delivery charges are entered in dollars; deposits are
// entered in dollars but stored in cents (same as stock_items.deposit_cents).
// A deposit of 0 means "no deposit for this customer"; blank means catalog.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Plus, Tags, Trash2, Truck, X } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import {
  PriceTier,
  CustomerItemPrice,
  listPriceTiers,
  createPriceTier,
  renamePriceTier,
  deletePriceTier,
  setTierItemPrice,
  setTierDeliveryPrices,
  removeTierItem,
  listCustomerItemPrices,
  setCustomerItemPrice,
  removeCustomerItemPrice,
  assignCustomerTier,
  setCustomerDeliveryPrices,
  getCustomerDeliveryPrices,
} from '../lib/customerPricing';
import { ProductPickerOverlay, PickerStockItem } from './orders/NewOrderModal';
import { loadStoreShippingConfig } from './orders/shippingConfig';
import { ShippingMethod, describeShippingPrice } from '../lib/storefront';

const Z = 100200;

const parseMoney = (text: string): number | null => {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const n = parseFloat(trimmed);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/** Small labeled $ input used for price/deposit cells. */
function MoneyInput({
  value,
  placeholder,
  onCommit,
  title,
  width = 'w-24',
}: {
  value: string;
  placeholder?: string;
  onCommit: (text: string) => void;
  title?: string;
  width?: string;
}) {
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);
  return (
    <div className="relative flex-shrink-0" title={title}>
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">$</span>
      <input
        type="text"
        inputMode="decimal"
        value={text}
        placeholder={placeholder}
        onChange={e => setText(e.target.value)}
        onBlur={() => onCommit(text)}
        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className={`${width} pl-5 pr-2 py-1.5 text-sm font-semibold text-violet-700 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 placeholder:font-normal placeholder:text-gray-400`}
      />
    </div>
  );
}

/** Per-method delivery override editor. Blank = the method's standard price. */
function DeliveryOverridesEditor({
  methods,
  values,
  disabled,
  onSave,
}: {
  methods: ShippingMethod[];
  values: Record<string, number>;
  disabled?: boolean;
  onSave: (next: Record<string, number>) => void;
}) {
  if (methods.length === 0) {
    return <p className="mt-2 text-xs text-gray-400">No delivery methods configured in Online Store settings.</p>;
  }
  return (
    <div className={`mt-3 rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
      {methods.map(m => {
        const desc = describeShippingPrice(m, null, undefined);
        const standardLabel = desc.state === 'priced' || desc.state === 'free' ? desc.label : 'Varies by area';
        return (
          <div key={m.name} className="px-4 py-2 flex items-center gap-3 text-sm">
            <Truck className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="text-gray-800 truncate flex-1">{m.name}</span>
            <span className="text-[11px] text-gray-400 flex-shrink-0">{standardLabel}</span>
            <MoneyInput
              value={values[m.name] != null ? values[m.name].toFixed(2) : ''}
              placeholder="Standard"
              title="Custom delivery charge — blank uses the standard price"
              onCommit={text => {
                const next = { ...values };
                const parsed = parseMoney(text);
                if (parsed == null) delete next[m.name];
                else next[m.name] = parsed;
                const changed = JSON.stringify(next) !== JSON.stringify(values);
                if (changed) onSave(next);
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

export function CustomerPricingCard({ businessId, customerId }: { businessId: string; customerId: string }) {
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState<PriceTier[]>([]);
  const [tierId, setTierId] = useState<string | null>(null);
  const [customPrices, setCustomPrices] = useState<CustomerItemPrice[]>([]);
  const [deliveryOwn, setDeliveryOwn] = useState<Record<string, number>>({});
  const [shipMethods, setShipMethods] = useState<ShippingMethod[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [showTierManager, setShowTierManager] = useState(false);
  // Product picked and awaiting its price/deposit before saving.
  const [pending, setPending] = useState<PickerStockItem | null>(null);
  const [pendingPrice, setPendingPrice] = useState('');
  const [pendingDeposit, setPendingDeposit] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [tierList, { data: cust }, prices, delivery, shipConfig] = await Promise.all([
        listPriceTiers(businessId),
        supabase.from('customers').select('price_tier_id').eq('id', customerId).maybeSingle(),
        listCustomerItemPrices(customerId),
        getCustomerDeliveryPrices(customerId),
        loadStoreShippingConfig(businessId).catch(() => null),
      ]);
      setTiers(tierList);
      setTierId((cust?.price_tier_id as string | null) ?? null);
      setCustomPrices(prices);
      setDeliveryOwn(delivery);
      setShipMethods(shipConfig?.shippingMethods || []);
    } catch {
      // Tables may not exist yet on an un-migrated env — card degrades silently.
    } finally {
      setLoading(false);
    }
  }, [businessId, customerId]);

  useEffect(() => { load(); }, [load]);

  const hasIndividual = customPrices.length > 0 || Object.keys(deliveryOwn).length > 0;
  const assignedTier = tiers.find(t => t.id === tierId) || null;

  const handleTierChange = async (value: string) => {
    setErr(null);
    const next = value || null;
    try {
      await assignCustomerTier(customerId, next);
      setTierId(next);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not update tier');
    }
  };

  const savePending = async () => {
    if (!pending) return;
    const price = parseMoney(pendingPrice);
    const deposit = parseMoney(pendingDeposit);
    if (price == null && deposit == null) { setErr('Enter a custom price and/or deposit'); return; }
    setSaving(true);
    setErr(null);
    try {
      await setCustomerItemPrice(businessId, customerId, pending.id, {
        ...(price != null ? { price } : {}),
        ...(deposit != null ? { deposit_cents: Math.round(deposit * 100) } : {}),
      });
      setPending(null);
      setPendingPrice('');
      setPendingDeposit('');
      setCustomPrices(await listCustomerItemPrices(customerId));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save price');
    } finally {
      setSaving(false);
    }
  };

  /** Commit a row edit. Clearing both fields removes the row. */
  const updateRow = async (row: CustomerItemPrice, patch: { price?: number | null; deposit_cents?: number | null }) => {
    const nextPrice = 'price' in patch ? patch.price ?? null : row.price;
    const nextDeposit = 'deposit_cents' in patch ? patch.deposit_cents ?? null : row.deposit_cents;
    setErr(null);
    try {
      if (nextPrice == null && nextDeposit == null) {
        await removeCustomerItemPrice(customerId, row.stock_item_id);
        setCustomPrices(prev => prev.filter(r => r.id !== row.id));
        return;
      }
      await setCustomerItemPrice(businessId, customerId, row.stock_item_id, { price: nextPrice, deposit_cents: nextDeposit });
      setCustomPrices(prev => prev.map(r => (r.id === row.id ? { ...r, price: nextPrice, deposit_cents: nextDeposit } : r)));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save');
    }
  };

  const removeRow = async (row: CustomerItemPrice) => {
    try {
      await removeCustomerItemPrice(customerId, row.stock_item_id);
      setCustomPrices(prev => prev.filter(r => r.id !== row.id));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not remove price');
    }
  };

  const saveDelivery = async (next: Record<string, number>) => {
    setErr(null);
    try {
      await setCustomerDeliveryPrices(customerId, next);
      setDeliveryOwn(next);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save delivery prices');
    }
  };

  const depositLabel = (cents: number | null | undefined) =>
    cents == null ? null : cents === 0 ? 'No deposit' : `$${(cents / 100).toFixed(2)} deposit`;

  return (
    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Pricing</h3>
          <p className="text-sm text-gray-500 mt-0.5">Custom product prices, deposits and delivery charges for this customer</p>
        </div>
        <button
          type="button"
          onClick={() => setShowTierManager(true)}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <Tags className="h-3.5 w-3.5" /> Manage tiers
        </button>
      </div>
      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {/* Tier assignment */}
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-900">Price tier</p>
                <p className="text-xs text-gray-500 mt-1">
                  {hasIndividual
                    ? 'This customer has individual custom pricing — remove it below to use a tier instead.'
                    : 'Assign a tier to apply its product and delivery prices to this customer.'}
                </p>
              </div>
              <select
                value={tierId || ''}
                onChange={e => handleTierChange(e.target.value)}
                disabled={hasIndividual}
                className="flex-shrink-0 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">No tier — catalog prices</option>
                {tiers.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            {/* Assigned tier's pricing, read-only */}
            {assignedTier && (
              <div className="mt-4 rounded-lg border border-violet-200 bg-violet-50/50 overflow-hidden">
                <div className="px-4 py-2 text-[12px] font-semibold text-violet-700 border-b border-violet-100">
                  {assignedTier.name}
                </div>
                {(assignedTier.items || []).length === 0 && Object.keys(assignedTier.delivery_prices || {}).length === 0 ? (
                  <p className="px-4 py-3 text-xs text-gray-500">Nothing priced in this tier yet — add products via Manage tiers.</p>
                ) : (
                  <div className="divide-y divide-violet-100">
                    {(assignedTier.items || []).map(it => (
                      <div key={it.id} className="px-4 py-2 flex items-center justify-between gap-3 text-sm">
                        <span className="text-gray-800 truncate">{it.item_name}</span>
                        <span className="flex items-baseline gap-2 flex-shrink-0">
                          {it.deposit_cents != null && (
                            <span className="text-[11px] font-medium text-amber-700">{depositLabel(it.deposit_cents)}</span>
                          )}
                          {it.price != null && it.catalog_price != null && it.catalog_price !== it.price && (
                            <span className="text-[11px] text-gray-400 line-through">${it.catalog_price.toFixed(2)}</span>
                          )}
                          {it.price != null && <span className="font-semibold text-violet-700">${it.price.toFixed(2)}</span>}
                        </span>
                      </div>
                    ))}
                    {Object.entries(assignedTier.delivery_prices || {}).map(([name, price]) => (
                      <div key={name} className="px-4 py-2 flex items-center justify-between gap-3 text-sm">
                        <span className="text-gray-800 truncate flex items-center gap-1.5">
                          <Truck className="h-3.5 w-3.5 text-gray-400" /> {name}
                        </span>
                        <span className="font-semibold text-violet-700">
                          {Number(price) === 0 ? 'FREE' : `$${Number(price).toFixed(2)}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Individual custom item pricing */}
            <div className="mt-5 pt-5 border-t border-gray-100">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">Custom product prices &amp; deposits</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {tierId
                      ? 'This customer is on a price tier — remove the tier above to set individual pricing.'
                      : 'Per-product price and container deposit, just for this customer. Deposit $0.00 = no deposit.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPicker(true)}
                  disabled={!!tierId}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Plus className="h-3.5 w-3.5" /> Add price
                </button>
              </div>

              {pending && (
                <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-800 truncate flex-1">{pending.name}</span>
                    <button onClick={() => { setPending(null); setPendingPrice(''); setPendingDeposit(''); }} className="p-1.5 text-gray-400 hover:text-gray-600">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-2 flex items-end gap-3 flex-wrap">
                    <label className="text-[11px] font-semibold text-gray-500">
                      Price {pending.price != null && <span className="font-normal">(catalog ${Number(pending.price).toFixed(2)})</span>}
                      <div className="relative mt-1">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">$</span>
                        <input
                          autoFocus
                          type="text"
                          inputMode="decimal"
                          value={pendingPrice}
                          onChange={e => setPendingPrice(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') savePending(); }}
                          className="w-24 pl-5 pr-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                          placeholder="Catalog"
                        />
                      </div>
                    </label>
                    <label className="text-[11px] font-semibold text-gray-500">
                      Deposit {Number(pending.deposit_cents) > 0 && <span className="font-normal">(catalog ${(Number(pending.deposit_cents) / 100).toFixed(2)})</span>}
                      <div className="relative mt-1">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={pendingDeposit}
                          onChange={e => setPendingDeposit(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') savePending(); }}
                          className="w-24 pl-5 pr-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                          placeholder="Catalog"
                        />
                      </div>
                    </label>
                    <button onClick={savePending} disabled={saving} className="px-3 py-1.5 text-[12px] font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                      Save
                    </button>
                  </div>
                </div>
              )}

              {customPrices.length > 0 && (
                <div className="mt-3 rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                  {customPrices.map(row => (
                    <div key={row.id} className="px-4 py-2 flex items-center gap-3 text-sm flex-wrap">
                      <span className="text-gray-800 truncate flex-1 min-w-[120px]">{row.item_name}</span>
                      {row.price != null && row.catalog_price != null && row.catalog_price !== row.price && (
                        <span className="text-[11px] text-gray-400 line-through flex-shrink-0">${row.catalog_price.toFixed(2)}</span>
                      )}
                      <MoneyInput
                        value={row.price != null ? row.price.toFixed(2) : ''}
                        placeholder={row.catalog_price != null ? row.catalog_price.toFixed(2) : 'Catalog'}
                        title="Custom price — blank uses the catalog price"
                        onCommit={text => {
                          const parsed = parseMoney(text);
                          if ((parsed ?? null) !== (row.price ?? null)) updateRow(row, { price: parsed });
                        }}
                      />
                      <MoneyInput
                        value={row.deposit_cents != null ? (row.deposit_cents / 100).toFixed(2) : ''}
                        placeholder={row.catalog_deposit_cents ? (row.catalog_deposit_cents / 100).toFixed(2) : 'No deposit'}
                        title="Custom deposit — blank uses the catalog deposit, $0.00 removes it"
                        width="w-20"
                        onCommit={text => {
                          const parsed = parseMoney(text);
                          const cents = parsed == null ? null : Math.round(parsed * 100);
                          if ((cents ?? null) !== (row.deposit_cents ?? null)) updateRow(row, { deposit_cents: cents });
                        }}
                      />
                      <button onClick={() => removeRow(row)} className="p-1.5 text-gray-400 hover:text-red-600 flex-shrink-0" title="Remove custom pricing">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Individual delivery charges */}
            <div className="mt-5 pt-5 border-t border-gray-100">
              <p className="text-sm font-medium text-gray-900">Custom delivery charges</p>
              <p className="text-xs text-gray-500 mt-1">
                {tierId
                  ? 'This customer is on a price tier — its delivery prices apply.'
                  : 'Override what this customer pays per delivery method. $0.00 = free delivery; blank = standard price.'}
              </p>
              <DeliveryOverridesEditor
                methods={shipMethods}
                values={deliveryOwn}
                disabled={!!tierId}
                onSave={saveDelivery}
              />
            </div>

            {err && <p className="mt-3 text-xs font-medium text-red-600">{err}</p>}
          </>
        )}
      </div>

      {showPicker && (
        <ProductPickerOverlay
          businessId={businessId}
          onPick={item => {
            setPending(item);
            setPendingPrice(item.price != null ? Number(item.price).toFixed(2) : '');
            setPendingDeposit('');
            setShowPicker(false);
          }}
          onClose={() => setShowPicker(false)}
          zIndex={Z}
        />
      )}

      {showTierManager && (
        <PriceTiersModal
          businessId={businessId}
          shipMethods={shipMethods}
          onClose={() => { setShowTierManager(false); load(); }}
        />
      )}
    </div>
  );
}

// ── Tier manager ──────────────────────────────────────────────────────────────

function PriceTiersModal({ businessId, shipMethods, onClose }: { businessId: string; shipMethods: ShippingMethod[]; onClose: () => void }) {
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState<PriceTier[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [pending, setPending] = useState<PickerStockItem | null>(null);
  const [pendingPrice, setPendingPrice] = useState('');
  const [pendingDeposit, setPendingDeposit] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await listPriceTiers(businessId);
      setTiers(list);
      setSelectedId(prev => prev && list.some(t => t.id === prev) ? prev : (list[0]?.id ?? null));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load tiers');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const selected = tiers.find(t => t.id === selectedId) || null;

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setErr(null);
    try {
      const tier = await createPriceTier(businessId, name);
      setNewName('');
      setTiers(prev => [...prev, tier].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedId(tier.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not create tier');
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async (name: string) => {
    if (!selected || !name.trim() || name.trim() === selected.name) return;
    try {
      await renamePriceTier(selected.id, name.trim());
      setTiers(prev => prev.map(t => (t.id === selected.id ? { ...t, name: name.trim() } : t)));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not rename tier');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await deletePriceTier(selected.id);
      setConfirmDelete(false);
      setSelectedId(null);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not delete tier');
    }
  };

  const savePending = async () => {
    if (!selected || !pending) return;
    const price = parseMoney(pendingPrice);
    const deposit = parseMoney(pendingDeposit);
    if (price == null && deposit == null) { setErr('Enter a custom price and/or deposit'); return; }
    setErr(null);
    try {
      await setTierItemPrice(businessId, selected.id, pending.id, {
        ...(price != null ? { price } : {}),
        ...(deposit != null ? { deposit_cents: Math.round(deposit * 100) } : {}),
      });
      setPending(null);
      setPendingPrice('');
      setPendingDeposit('');
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save price');
    }
  };

  /** Commit a tier item edit. Clearing both fields removes the item. */
  const updateItem = async (stockItemId: string, patch: { price?: number | null; deposit_cents?: number | null }) => {
    if (!selected) return;
    const current = (selected.items || []).find(i => i.stock_item_id === stockItemId);
    if (!current) return;
    const nextPrice = 'price' in patch ? patch.price ?? null : current.price;
    const nextDeposit = 'deposit_cents' in patch ? patch.deposit_cents ?? null : current.deposit_cents;
    setErr(null);
    try {
      if (nextPrice == null && nextDeposit == null) {
        await removeTierItem(selected.id, stockItemId);
        setTiers(prev => prev.map(t => t.id !== selected.id ? t : {
          ...t,
          items: (t.items || []).filter(i => i.stock_item_id !== stockItemId),
        }));
        return;
      }
      await setTierItemPrice(businessId, selected.id, stockItemId, { price: nextPrice, deposit_cents: nextDeposit });
      setTiers(prev => prev.map(t => t.id !== selected.id ? t : {
        ...t,
        items: (t.items || []).map(i => (i.stock_item_id === stockItemId ? { ...i, price: nextPrice, deposit_cents: nextDeposit } : i)),
      }));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save');
    }
  };

  const removeItem = async (stockItemId: string) => {
    if (!selected) return;
    try {
      await removeTierItem(selected.id, stockItemId);
      setTiers(prev => prev.map(t => t.id !== selected.id ? t : {
        ...t,
        items: (t.items || []).filter(i => i.stock_item_id !== stockItemId),
      }));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not remove product');
    }
  };

  const saveTierDelivery = async (next: Record<string, number>) => {
    if (!selected) return;
    setErr(null);
    try {
      await setTierDeliveryPrices(selected.id, next);
      setTiers(prev => prev.map(t => (t.id === selected.id ? { ...t, delivery_prices: next } : t)));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save delivery prices');
    }
  };

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: Z }}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Price tiers</h3>
            <p className="text-sm text-gray-500 mt-0.5">Group product, deposit and delivery prices into tiers you can assign to customers</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* Create + select */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
                  placeholder="New tier name (e.g. Wholesale)"
                  className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />
                <button
                  onClick={handleCreate}
                  disabled={creating || !newName.trim()}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
                >
                  <Plus className="h-4 w-4" /> Create
                </button>
              </div>

              {tiers.length === 0 ? (
                <div className="py-10 text-center">
                  <Tags className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-500">No price tiers yet</p>
                  <p className="text-xs text-gray-400 mt-1">Create one above, add product prices, then assign it to customers</p>
                </div>
              ) : (
                <>
                  <div className="flex gap-1.5 overflow-x-auto mt-4 pb-1">
                    {tiers.map(t => (
                      <button
                        key={t.id}
                        onClick={() => { setSelectedId(t.id); setConfirmDelete(false); setPending(null); }}
                        className={`px-3 py-1.5 rounded-full text-[12px] font-semibold whitespace-nowrap transition-colors ${
                          selectedId === t.id ? 'bg-violet-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {t.name} · {t.items?.length || 0}
                      </button>
                    ))}
                  </div>

                  {selected && (
                    <div className="mt-4 rounded-xl border border-gray-200 overflow-hidden">
                      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                        <input
                          type="text"
                          defaultValue={selected.name}
                          key={selected.id}
                          onBlur={e => handleRename(e.target.value)}
                          className="flex-1 px-2 py-1.5 text-sm font-semibold text-gray-900 bg-transparent border border-transparent rounded-lg hover:border-gray-200 focus:border-gray-300 focus:bg-white focus:outline-none"
                        />
                        {confirmDelete ? (
                          <span className="flex items-center gap-1.5">
                            <button onClick={handleDelete} className="px-2.5 py-1.5 text-[12px] font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700">
                              Delete tier
                            </button>
                            <button onClick={() => setConfirmDelete(false)} className="px-2.5 py-1.5 text-[12px] font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg">
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <button onClick={() => setConfirmDelete(true)} className="p-2 text-gray-400 hover:text-red-600 rounded-lg" title="Delete tier (customers on it revert to catalog prices)">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      <div className="p-4">
                        <button
                          onClick={() => setShowPicker(true)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                        >
                          <Plus className="h-3.5 w-3.5" /> Add product to tier
                        </button>

                        {pending && (
                          <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-800 truncate flex-1">{pending.name}</span>
                              <button onClick={() => { setPending(null); setPendingPrice(''); setPendingDeposit(''); }} className="p-1.5 text-gray-400 hover:text-gray-600">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                            <div className="mt-2 flex items-end gap-3 flex-wrap">
                              <label className="text-[11px] font-semibold text-gray-500">
                                Price {pending.price != null && <span className="font-normal">(catalog ${Number(pending.price).toFixed(2)})</span>}
                                <div className="relative mt-1">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">$</span>
                                  <input
                                    autoFocus
                                    type="text"
                                    inputMode="decimal"
                                    value={pendingPrice}
                                    onChange={e => setPendingPrice(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') savePending(); }}
                                    className="w-24 pl-5 pr-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                                    placeholder="Catalog"
                                  />
                                </div>
                              </label>
                              <label className="text-[11px] font-semibold text-gray-500">
                                Deposit {Number(pending.deposit_cents) > 0 && <span className="font-normal">(catalog ${(Number(pending.deposit_cents) / 100).toFixed(2)})</span>}
                                <div className="relative mt-1">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">$</span>
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={pendingDeposit}
                                    onChange={e => setPendingDeposit(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') savePending(); }}
                                    className="w-24 pl-5 pr-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                                    placeholder="Catalog"
                                  />
                                </div>
                              </label>
                              <button onClick={savePending} className="px-3 py-1.5 text-[12px] font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                                Save
                              </button>
                            </div>
                          </div>
                        )}

                        {(selected.items || []).length === 0 ? (
                          <p className="mt-3 text-xs text-gray-500">No products in this tier yet.</p>
                        ) : (
                          <div className="mt-3 rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                            {(selected.items || []).map(it => (
                              <div key={it.id} className="px-4 py-2 flex items-center gap-3 text-sm flex-wrap">
                                <span className="text-gray-800 truncate flex-1 min-w-[120px]">{it.item_name}</span>
                                {it.price != null && it.catalog_price != null && it.catalog_price !== it.price && (
                                  <span className="text-[11px] text-gray-400 line-through flex-shrink-0">${it.catalog_price.toFixed(2)}</span>
                                )}
                                <MoneyInput
                                  value={it.price != null ? it.price.toFixed(2) : ''}
                                  placeholder={it.catalog_price != null ? it.catalog_price.toFixed(2) : 'Catalog'}
                                  title="Tier price — blank uses the catalog price"
                                  onCommit={text => {
                                    const parsed = parseMoney(text);
                                    if ((parsed ?? null) !== (it.price ?? null)) updateItem(it.stock_item_id, { price: parsed });
                                  }}
                                />
                                <MoneyInput
                                  value={it.deposit_cents != null ? (it.deposit_cents / 100).toFixed(2) : ''}
                                  placeholder={it.catalog_deposit_cents ? (it.catalog_deposit_cents / 100).toFixed(2) : 'No deposit'}
                                  title="Tier deposit — blank uses the catalog deposit, $0.00 removes it"
                                  width="w-20"
                                  onCommit={text => {
                                    const parsed = parseMoney(text);
                                    const cents = parsed == null ? null : Math.round(parsed * 100);
                                    if ((cents ?? null) !== (it.deposit_cents ?? null)) updateItem(it.stock_item_id, { deposit_cents: cents });
                                  }}
                                />
                                <button onClick={() => removeItem(it.stock_item_id)} className="p-1.5 text-gray-400 hover:text-red-600 flex-shrink-0" title="Remove from tier">
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Tier delivery charges */}
                        <div className="mt-5 pt-4 border-t border-gray-100">
                          <p className="text-[13px] font-semibold text-gray-900">Delivery charges</p>
                          <p className="text-xs text-gray-500 mt-0.5">What customers on this tier pay per delivery method. $0.00 = free; blank = standard.</p>
                          <DeliveryOverridesEditor
                            methods={shipMethods}
                            values={selected.delivery_prices || {}}
                            onSave={saveTierDelivery}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {err && <p className="mt-3 text-xs font-medium text-red-600">{err}</p>}
            </>
          )}
        </div>
      </div>

      {showPicker && (
        <ProductPickerOverlay
          businessId={businessId}
          onPick={item => {
            setPending(item);
            setPendingPrice(item.price != null ? Number(item.price).toFixed(2) : '');
            setPendingDeposit('');
            setShowPicker(false);
          }}
          onClose={() => setShowPicker(false)}
          zIndex={Z + 20}
        />
      )}
    </div>,
    document.body
  );
}
